import { Link } from 'react-router-dom';
import { t, tn } from '@app/i18n';
import { Alert, Badge, Card, InfoTip, type BadgeTone } from '@zarya/ui';
import { dataHour } from '@app/format';
import { useDiscoveryStatus } from '@app/discovery';
import { useDiscoveryConfig } from '@app/main/TenantSettings/TenantSettings.api';
import s from './Overview.module.css';

/**
 * Card **Discovery** da segunda linha de T3 (Lote 2b).
 *
 * Quatro fatos, e são os quatro que o operador pergunta ao abrir a tela: se a
 * varredura está ligada, de quanto em quanto tempo ela roda, quando foi a última e
 * o que ela rendeu.
 *
 * **Não há leitura nova por causa deste card.** As duas fontes já existem e já são
 * consultadas pelo console: `getDiscoveryConfig` (estado, intervalo, última
 * execução, resultado — é o card de T21) e `getDiscoveryStatus` (se há varredura
 * correndo AGORA, a mesma que o indicador de tarefas usa e que a moldura já pede em
 * toda tela). As duas são locais: nenhuma delas fala com o middleware, e por isso o
 * card desenha inteiro no primeiro instante da tela.
 *
 * O vocabulário é o de T21 de propósito — «ativo», «pausado», «em execução»,
 * «{n} imagens varridas» —, com chaves próprias no catálogo `overview`. Duas telas
 * dizendo a mesma coisa com palavras diferentes é o defeito que a auditoria de copy
 * de 08/09 existiu para fechar.
 */

/** Estado da varredura, na ordem em que ele vence: correndo › ativo › pausado. */
function stateView(running: boolean, active: boolean): { label: string; tone: BadgeTone } {
  if (running) return { label: t('overview.discovery.state.running'), tone: 'primary' };
  if (active) return { label: t('overview.discovery.state.active'), tone: 'success' };
  return { label: t('overview.discovery.state.paused'), tone: 'warning' };
}

interface RowProps {
  label: string;
  tip: string;
  children: React.ReactNode;
}

function Row({ label, tip, children }: RowProps) {
  return (
    <div className={s.linha}>
      <span className={s.linhaRotulo}>
        {label}
        <InfoTip text={tip} label={t('overview.discovery.rowTipLabel', { label })} size="xs" />
      </span>
      <span className={s.linhaValor}>{children}</span>
    </div>
  );
}

export function DiscoveryCard() {
  const config = useDiscoveryConfig();
  const status = useDiscoveryStatus();

  const running = status.data?.running ?? config.data?.running ?? false;
  const active = config.data?.active ?? false;
  const state = stateView(running, active);

  // A última execução e o resultado saem do estado vivo quando ele já chegou —
  // uma varredura iniciada por outra pessoa termina e o card se atualiza sozinho,
  // sem F5 —, e do documento de configuração enquanto ele não chegou.
  const lastAt = status.data?.last_execution_at ?? config.data?.last_execution_at;
  const result = status.data?.last_result ?? config.data?.last_result;
  const when = dataHour(lastAt);

  return (
    <Card
      title={t('overview.discovery.title')}
      tip={t('overview.discovery.tip')}
      toolbar={
        <Link className={s.linkAcao} to="/tenant">
          {t('overview.discovery.link')}
        </Link>
      }
    >
      {config.isError ? (
        <Alert tone="warning">{t('overview.discovery.error')}</Alert>
      ) : (
        <>
          <Row
            label={t('overview.discovery.row.state')}
            tip={t('overview.discovery.row.state.tip')}
          >
            <Badge tone={state.tone}>{state.label}</Badge>
          </Row>

          <Row
            label={t('overview.discovery.row.interval')}
            tip={t('overview.discovery.row.interval.tip')}
          >
            {/* Sem o documento lido, o intervalo é «não lido» e **não** o default de
                5 do contrato: um número plausível aqui é uma afirmação sobre a
                configuração do cliente que ninguém verificou. */}
            {config.data ? (
              tn('overview.discovery.row.interval.value', config.data.interval_min)
            ) : (
              <span className={s.vazio}>{t('overview.discovery.unread')}</span>
            )}
          </Row>

          <Row
            label={t('overview.discovery.row.lastSweep')}
            tip={t('overview.discovery.row.lastSweep.tip')}
          >
            {when ?? <span className={s.vazio}>{t('overview.discovery.never')}</span>}
          </Row>

          <Row
            label={t('overview.discovery.row.result')}
            tip={t('overview.discovery.row.result.tip')}
          >
            <DiscoveryResult result={result} />
          </Row>

          <SweepWarnings avisos={result?.warnings} />
        </>
      )}
    </Card>
  );
}

/**
 * **Todos** os avisos da última varredura, um por item — nunca só o primeiro.
 *
 * A varredura emite mais de um aviso desde o Lote 2b (o `a2a_completado` do sweep
 * e o «corte de tags veio cheio» chegaram juntos), e mostrar `avisos[0]` esconde o
 * segundo sem que nada fique vermelho: a tela continua dizendo «Varredura com
 * aviso», no singular, sobre dois problemas diferentes. É a lista que cresceu, não
 * uma regressão — e é por isso que a correção é estrutural (lista) e não pontual.
 *
 * O TEXTO de cada aviso é **dado do servidor** (`warnings[]` é `[]string` livre em
 * `usecases/discovery`), e sai cru, como o rótulo do backend na trilha de T23. Do
 * catálogo vem só a moldura — o título com a contagem. Traduzir o aviso exigiria
 * que o contrato mandasse um código, e isso é do dono do discovery.
 */
function SweepWarnings({ avisos }: { avisos?: string[] }) {
  if (!avisos?.length) return null;
  return (
    <Alert tone="warning" title={tn('overview.discovery.warnings.title', avisos.length)}>
      <ul className={s.avisos}>
        {avisos.map((aviso) => (
          <li key={aviso}>{aviso}</li>
        ))}
      </ul>
    </Alert>
  );
}

type Result = NonNullable<ReturnType<typeof useDiscoveryConfig>['data']>['last_result'];

/**
 * O resultado da última varredura. Erro **vence** as contagens: uma varredura que
 * falhou no meio pode ter varrido 3 imagens, e mostrar «3 imagens varridas» sem
 * dizer que ela falhou é a meia-verdade que faz o operador não ir olhar.
 */
function DiscoveryResult({ result }: { result: Result }) {
  if (!result) return <span className={s.vazio}>{t('overview.discovery.never')}</span>;

  if (result.error_code) {
    return (
      <>
        <Badge tone="danger">{t('overview.discovery.result.failed')}</Badge>
        <span className={s.mono}>{result.error_code}</span>
      </>
    );
  }

  // **Sem «{n} imagens varridas» aqui, de propósito.** Esse número já está na linha
  // do cabeçalho da tela («Último Discovery {quando} · {n} imagens varridas»), e o
  // toast do «Discovery agora» o repete uma terceira vez quando a varredura acaba.
  // O mesmo fato escrito três vezes na mesma tela não informa mais — só faz o
  // operador procurar a diferença entre eles. O que o card acrescenta é o que o
  // cabeçalho não diz: quanto virou release nova e quanto disso caiu na fila.
  const parts = [
    tn('overview.discovery.result.releases', result.new_releases ?? 0),
    tn('overview.discovery.result.queued', result.queued ?? 0),
  ];
  return (
    <>
      <span>{parts.join(' · ')}</span>
      {/* O selo diz QUANTOS; a lista abaixo do card diz QUAIS (ver `SweepWarnings`).
          Os dois, e não um: o selo mantém a linha do resultado escaneável, e a
          lista é o que impede o segundo aviso de sumir. */}
      {result.warnings?.length ? (
        <Badge tone="warning">
          {tn('overview.discovery.result.warnings', result.warnings.length)}
        </Badge>
      ) : null}
    </>
  );
}
